import { IReportPayload, ReportActivity } from './../../common/types/report.types'
import { IMentionable } from 'common/types/mentions.types'
import {
  BackendContent,
  ContentActivity,
  ContentFeed,
  Content,
  CommentsList,
  Reaction,
  Report,
  UserReactionParams,
  Insight,
  ContentLog,
  ReportAction,
  ReportedContent,
  BackendPaginatedResponse,
  UnBlockOrUnSuspendUserPayload,
  ReportActivityLog,
  ReportLog
} from 'common/types'

import { useHttp } from 'hooks/use-http/useHttp'
import { useAnalytics } from 'hooks/use-analytics/useAnalytics'
import { useAuth } from 'pages/auth/hooks/useAuth'

export const useContentApi = () => {
  const { get, post, destroy, put } = useHttp()
  const { logEventAnalytics } = useAnalytics()
  const { backendPartner, backendUser } = useAuth()

  const fetchAllContent = async (userId: string, page: number = 1, pageSize: number = 5) => {
    const response: ContentFeed = await get(
      `content/${userId}/feed/all?page=${page}&pageSize=${pageSize}&dir=desc&sort=created_at`,
      { useApiKey: true }
    )
    return response
  }

  const fetchTaggedContent = async (page: number = 1, pageSize: number = 5, topic = '') => {
    const response: ContentFeed = await get(
      `/content/hashtag/${topic}?page=${page}&pageSize=${pageSize}&dir=desc&sort=created_at`,
      { useApiKey: true }
    )
    return response
  }

  const createContent = async (userId: string, content: BackendContent) => {
    const response: BackendContent = await post(`content/${userId}`, content, { dieSilent: false })

    const partnerLog = {}
    const userLog = {}

    partnerLog['Partner ID'] = backendPartner?.id || ''
    partnerLog['Account Type'] = backendUser?.role || ''
    partnerLog['Company Name'] = backendPartner?.name || ''

    if (content?.mentions?.length) {
      logEventAnalytics(
        backendUser?.partnerId ? 'Partner' : backendUser?.role,
        'Profile tagged',
        'mixpanel',
        'Profile tagged',
        {
          'Profile ID tagged': content?.mentions?.map(mention => mention?.id),
          'Content type': content?.type || '',
          'User ID': backendUser?.id || '',
          ...partnerLog,
          ...userLog
        },
        backendUser?.id || '',
        {
          $name: `${backendUser?.firstName} ${backendUser?.lastName}`.trim() || '',
          $email: backendUser?.username || ''
        }
      )
    }

    return response
  }

  const editContent = async (userId: string, contentId: string, content: BackendContent) => {
    const response: BackendContent = await put(`/content/${userId}/${contentId}`, content, { dieSilent: true })
    return response
  }

  const deleteContent = async (userId: string, contentId: string) => {
    const response: any = await destroy(`content/${userId}/${contentId}`, { dieSilent: true })
    return response
  }

  const getContentActivityByContent = async (contentId: string) => {
    const response: ContentActivity = await get(`content/${contentId}/contentActivity`)
    return response
  }

  const getContentById = async (contentId: string) => {
    const response: Content = await get(`content/${contentId}`, { dieSilent: true })
    return response
  }

  const fetchMyPosts = async (userId: string, page: number = 1, pageSize: number = 5) => {
    const response: ContentFeed = await get(
      `/content/${userId}/posts?page=${page}&pageSize=${pageSize}&dir=desc&sort=created_at`
    )
    return response
  }

  const fetchComments = async (userId: string, contentId: string, page: number = 1) => {
    if (contentId) {
      const response: CommentsList = await get(
        `/content/${userId}/${contentId}/feed/comment?page=${page}&pageSize=5&dir=desc&sort=created_at`
      )
      return response
    }
  }

  const fetchCommentCount = async (contentId: string) => {
    if (contentId) {
      const response: CommentsList = await get(`/content/${contentId}/comment/count`)
      return response
    }
  }

  const createComment = async (
    userId: string,
    contentId: string,
    comment: string,
    partnerId?: string,
    mentions?: IMentionable[],
    tags?: string[]
  ) => {
    const response: BackendContent = await post(
      `content-activity/comment/${userId}/${contentId}`,
      { typeValue: comment, partnerId, mentions, tags: [] },
      { useApiKey: true, dieSilent: true }
    )
    const partnerLog = {}
    const userLog = {}

    if (backendUser?.partnerId) {
      partnerLog['Partner ID'] = backendPartner?.id
      partnerLog['Company Name'] = backendPartner?.name
      partnerLog['Account Type'] = 'Partner'
    } else {
      userLog['Account Type'] = backendUser?.role
    }

    logEventAnalytics(
      backendUser?.partnerId ? 'Partner' : backendUser?.role,
      'Profile tagged',
      'mixpanel',
      'Profile tagged',
      {
        'Profile ID tagged': mentions?.map(mention => mention?.id),
        'Content type': 'Comment',
        'User ID': backendUser?.id,
        ...partnerLog,
        ...userLog
      },
      backendUser?.id,
      {
        $name: `${backendUser?.firstName} ${backendUser?.lastName}`,
        $email: backendUser?.username
      }
    )
    return response
  }

  const updateComment = async (
    userId: string,
    commentId: string,
    comment: string,
    tags: string[],
    mentions?: IMentionable[]
  ) => {
    const response: Comment = await put(
      `content-activity/comment/${userId}/${commentId}`,
      { typeValue: comment, tags: [], mentions },
      { useApiKey: true, dieSilent: true }
    )
    return response
  }

  const deleteComment = async (userId: string, commentSiblingContentId: string, commentId: string) => {
    const response: Comment = await destroy(
      `content-activity/comment/${userId}/${commentSiblingContentId}/${commentId}`
    )
    return response
  }

  const createReaction = async (userId: string, contentId: string, reactionString: string, partnerId?: string) => {
    const response: ContentActivity = await post(
      `content-activity/reaction/${userId}/${contentId}`,
      { typeValue: reactionString, partnerId },
      { dieSilent: true }
    )
    return response
  }

  const fetchReactionCount = async (contentId: string) => {
    const response: Reaction[] = await get(`/content-activity/${contentId}`, { dieSilent: true })
    return response
  }

  const fetchUserReaction = async (userId: string, contentId: string) => {
    const response: ContentActivity = await get(`content/user/${userId}/${contentId}/reaction`, { dieSilent: true })
    return response
  }

  const reportContent = async (userId: string, reportPayload: IReportPayload) => {
    const response: Report = await post(`report/user/${userId}`, reportPayload, { dieSilent: true })
    return response
  }

  const fetchReportByContentId = async (contentId: string) => {
    const response: ReportedContent[] = await get(`report/content/${contentId}`, { dieSilent: true })
    return response
  }

  const getPost = async (contentId: string) => {
    if (contentId) {
      const response: Content = await get(`content/post/${contentId}`, { dieSilent: false })
      return response
    }
  }

  const createSharePost = async (
    userId: string,
    contentId: string,
    description: string,
    partnerId?: string,
    mentions?: IMentionable[],
    tags?: string[]
  ) => {
    const response: BackendContent = await post(
      `content-activity/share/${userId}/${contentId}`,
      { typeValue: description, partnerId, mentions, tags },
      { dieSilent: true }
    )
    return response
  }

  const editSharedPost = async (
    userId: string,
    sharedContentId: string,
    description: string,
    mentions?: IMentionable[],
    tags?: string[]
  ) => {
    const response: BackendContent = await put(
      `content-activity/share/${userId}/${sharedContentId}`,
      { typeValue: description, mentions, tags },
      { useApiKey: true, dieSilent: true }
    )
    return response
  }

  const fetchReactionsByUsers = async (
    userId: string,
    parentContentId: string,
    page: number = 1,
    pageSize: number = 5,
    type?: string
  ) => {
    const response: UserReactionParams = await get(
      `content/${userId}/${parentContentId}/reactions/users?page=${page}&pageSize=${pageSize}&dir=desc&sort=created_at&reactionType=${
        type ? type : ''
      }`,
      { dieSilent: false }
    )
    return response
  }

  const fetchInsight = async (userId: string) => {
    const response: Insight = await get(`user/${userId}/engagement-metrics`, { dieSilent: false })
    return response
  }

  const fetchPartnerPosts = async (partnerId: string, page: number = 1, pageSize: number = 5) => {
    const response: ContentFeed = await get(
      `/content/partner/${partnerId}/posts?page=${page}&pageSize=${pageSize}&dir=desc&sort=created_at`
    )
    return response
  }

  const fetchContentLogsById = async (contentId: string) => {
    const response: ContentLog[] = await get(`/content/content-logs/${contentId}`)
    return response
  }

  const getReportById = async (reportId: string) => {
    const response = await get(`report/${reportId}`, { dieSilent: true })
    return response
  }

  const fetchAllReportedUsers = async (userId: string, page: number, pageSize: number) => {
    const response = await get(
      `blocked-user/admin/${userId}?page=${page}&pageSize=${pageSize}&dir=desc&sort=created_at`,
      {
        useApiKey: true,
        dieSilent: true
      }
    )
    return response
  }

  const fetchAllSearchedReportedUsers = async (
    userId: string,
    page: number,
    pageSize: number,
    searchCriteria?: string,
    status?: string
  ) => {
    const response = await get(
      `/blocked-user/admin/${userId}/search?page=${page}&pageSize=${pageSize}&dir=asc&sort=created_at&searchCriteria=${
        searchCriteria || ''
      }&status=${status}&fromDate=&toDate=`,
      {
        useApiKey: true,
        dieSilent: true
      }
    )
    return response
  }

  const reportedUserActions = async (blockedUserId: string, userId: string, payload: UnBlockOrUnSuspendUserPayload) => {
    const response = await put(`blocked-user/${blockedUserId}/admin/${userId}/unblock`, payload, {
      useApiKey: true,
      dieSilent: true
    })
    return response
  }
  const fetchAllReports = async (
    userId: string,
    page: number,
    pageSize: number,
    searchCriteria?: string,
    status?: string,
    type?: string
  ) => {
    const response: BackendPaginatedResponse<ReportedContent> = await get(
      `reported-contents/search?page=${page}&pageSize=${pageSize}&dir=desc&sort=reported_contents.created_at`,
      { dieSilent: true }
    )
    return response
  }

  const fetchReportsByContentId = async (contentId: string) => {
    const response: ReportedContent[] = await get(`report/content/${contentId}`, { dieSilent: true })
    return response
  }

  const reportedContentActions = async (userId: string, payload: ReportActivity) => {
    const response = await post(`report-activity/admin/${userId}`, payload, { dieSilent: true })
    return response
  }

  const deleteReportedContent = async (userId: string, contentId: string) => {
    const response = await destroy(`content/${contentId}/admin/${userId}`, { dieSilent: true })
    return response
  }


  const fetchReportActivityLogs = async (page: number, pageSize: number, contentId: string) => {
    const response: BackendPaginatedResponse<ReportActivityLog[]> = await get(`/report-activity/reported-content/${contentId}?page=${page}&pageSize=${pageSize}&dir=desc&sort=created_at`,{ dieSilent: true })
    return response
  }

  const fetchReportLogs = async (page: number, pageSize: number, contentId: string) => {
    const response: BackendPaginatedResponse<ReportLog[]> = await get(`/report/content/${contentId}?page=${page}&pageSize=${pageSize}&dir=desc&sort=created_at`,{ dieSilent: true, useApiKey: true })
    return response
  }

  return {
    createContent,
    getContentActivityByContent,
    fetchAllContent,
    createComment,
    getContentById,
    deleteContent,
    fetchMyPosts,
    fetchComments,
    createReaction,
    fetchReactionCount,
    fetchUserReaction,
    deleteComment,
    reportContent,
    updateComment,
    getPost,
    createSharePost,
    editContent,
    editSharedPost,
    fetchCommentCount,
    fetchReactionsByUsers,
    fetchInsight,
    fetchPartnerPosts,
    fetchContentLogsById,
    fetchReportByContentId,
    fetchAllReports,
    getReportById,
    fetchAllReportedUsers,
    reportedUserActions,
    reportedContentActions,
    fetchAllSearchedReportedUsers,
    deleteReportedContent,
    fetchReportsByContentId,
    fetchTaggedContent,
    fetchReportActivityLogs,
    fetchReportLogs,
  } as const
}
